import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { Rangoli, Lantern, Marigold } from "./motifs";
import { scrollToId, WA_DEFAULT } from "@/lib/site";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1 -mb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.15, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const SwayLantern = ({ className, delay = 0 }) => (
    <motion.div
        className={`absolute origin-top ${className}`}
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0, rotate: [-2.5, 2.5, -2.5] }}
        transition={{
            opacity: { duration: 1, delay },
            y: { duration: 1, delay },
            rotate: { duration: 7, repeat: Infinity, ease: "easeInOut", delay },
        }}
        aria-hidden="true"
    >
        <Lantern className="w-full h-full text-[#472500]/55" />
    </motion.div>
);

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yMotif = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const rotateMotif = useTransform(scrollYProgress, [0, 1], [0, 40]);
    const yText = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden"
        >
            {/* parallax rangoli backdrop */}
            <motion.div
                style={{ y: yMotif, rotate: rotateMotif }}
                className="absolute -right-[12%] sm:-right-[6%] top-1/2 -translate-y-1/2 w-[80vmin] h-[80vmin] pointer-events-none"
                aria-hidden="true"
            >
                <Rangoli className="w-full h-full text-[#472500] opacity-[0.09] animate-spin-slower" />
            </motion.div>
            <motion.div
                style={{ y: yMotif }}
                className="absolute -left-[18%] -bottom-[22%] w-[55vmin] h-[55vmin] pointer-events-none"
                aria-hidden="true"
            >
                <Rangoli className="w-full h-full text-[#E2BA03] opacity-[0.14] animate-spin-slower" />
            </motion.div>

            {/* swaying lanterns */}
            <SwayLantern className="top-[76px] right-[6%] w-10 sm:w-14 h-24 sm:h-32 hidden md:block" delay={1.1} />
            <SwayLantern className="top-[76px] right-[14%] w-8 sm:w-10 h-20 sm:h-24 hidden lg:block" delay={1.35} />

            <motion.div
                style={{ y: yText, opacity: fade }}
                className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full"
            >
                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-3 text-xs sm:text-sm uppercase tracking-[0.35em] text-[#472500]/60 font-medium"
                    data-testid="hero-eyebrow"
                >
                    <Marigold className="w-5 h-5 text-[#EA580C]" />
                    Point Cook, VIC · Delivering Australia-wide
                </motion.p>

                <h1
                    data-testid="hero-headline"
                    className="font-display text-[13.5vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem] leading-[0.98] mt-8 text-[#472500]"
                >
                    <MaskedLine delay={0.5}>Traditional</MaskedLine>
                    <MaskedLine delay={0.62}>
                        Indian <em className="italic text-[#E2BA03] [text-shadow:0_1px_0_#472500]">Charm</em>
                    </MaskedLine>
                    <MaskedLine delay={0.74}>for Modern Homes.</MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 max-w-xl text-base sm:text-lg text-[#472500]/75 leading-relaxed"
                    data-testid="hero-subheadline"
                >
                    <span className="font-display italic text-[#472500]">Handmade. Heartmade.</span>
                    <br />
                    Every celebration, styled just right — festive décor, pooja essentials and gifting,
                    handpicked in Point Cook and sent across Australia.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-12 flex flex-wrap items-center gap-4"
                >
                    <a
                        href={WA_DEFAULT}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-whatsapp-cta"
                        className="group inline-flex items-center gap-3 rounded-full bg-[#E2BA03] text-[#472500] font-semibold px-8 py-4 text-base transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-12px_rgba(226,186,3,0.7)]"
                    >
                        <MessageCircle size={19} strokeWidth={2.2} className="transition-transform duration-300 group-hover:-rotate-12" />
                        WhatsApp Us to Order
                    </a>
                    <button
                        onClick={() => scrollToId("#collections")}
                        data-testid="hero-explore-cta"
                        className="group inline-flex items-center gap-3 rounded-full border border-[#472500]/40 text-[#472500] font-medium px-8 py-4 text-base transition-all duration-300 hover:border-[#472500] hover:bg-[#472500] hover:text-[#F0EEE8]"
                    >
                        Explore the Collection
                        <ArrowDown size={17} className="transition-transform duration-300 group-hover:translate-y-1" />
                    </button>
                </motion.div>
            </motion.div>

            {/* scroll cue */}
            <motion.button
                onClick={() => scrollToId("#collections")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                style={{ opacity: fade }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#472500]/50 hover:text-[#472500] transition-colors"
                data-testid="hero-scroll-cue"
                aria-label="Scroll to collections"
            >
                <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
                    <ArrowDown size={16} />
                </motion.span>
            </motion.button>
        </section>
    );
};

export default Hero;
